import React, { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
// eslint-disable-next-line no-restricted-imports -- TODO: update to `next/navigation` before using this component in App Router
import { useRouter } from "next/router";

import { CityInfo } from "@/api/requests/getCityStateGeocode";
import { getDeliveryServiceArea } from "@/api/requests/getDeliveryServiceArea";
import {
  DispensaryV3,
  DispensaryV3MerchandisedCampaign,
} from "@/custom-types/DispensariesV3";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useSelector } from "@/redux/hooks";
import {
  getCoordinates,
  getCountryCode,
  getIsValidStreetAddress,
} from "@/redux/selectors/location";
import { getDispensaryUrl } from "@/utils/dispensaryUrls";
import logError from "@/utils/logError";
import { isPlatinum } from "@/utils/tiers";

import AddressCaptureModal from "@/components/AddressCaptureModal";
import DispensaryCard from "@/components/botanic/DispensaryCard";
import TrackImpression from "@/components/TrackImpression";

const PreaddressDispensaryCards: React.FC<{
  className?: string;
  dispensaries: DispensaryV3[];
  geoCityResult?: CityInfo;
  headingOverride?: string | null;
  setAttemptedDispensaryName: (name: string) => void;
  setDispensaryLoadLock: (lock: boolean) => void;
}> = ({
  className,
  dispensaries,
  geoCityResult,
  headingOverride,
  setAttemptedDispensaryName,
  setDispensaryLoadLock,
}) => {
  const router = useRouter();
  const { publishEvent } = useEventTracker();

  const coordinates = useSelector(getCoordinates);
  const countryCode = useSelector(getCountryCode);
  const isValidAddress = useSelector(getIsValidStreetAddress);

  const [selectedDispensary, setSelectedDispensary] = useState<DispensaryV3>();
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  const modalReturnRef = React.useRef<HTMLElement>(null);

  const tryDeliveryFromDispensary = useCallback(
    async (selectedDispensary: DispensaryV3) => {
      try {
        const deliveryServiceArea = await getDeliveryServiceArea({
          countryCode,
          dispensaryId: selectedDispensary?.id,
          ...coordinates,
        });
        // * Push the user to this dispensaries page.
        if (deliveryServiceArea?.userInDeliveryServiceArea) {
          router.push(
            getDispensaryUrl({
              countryCode,
              slug: selectedDispensary.slug,
              tab: "menu",
            }),
          );
          return;
        }
      } catch {
        logError("Error processing data", {
          functionName: "tryDeliveryFromDispensary",
        });
      }
      // * Notify higher components that delivery was
      // * attempted for this dispensary.
      setAttemptedDispensaryName(selectedDispensary.name);
      setDispensaryLoadLock(false);
    },
    [
      coordinates,
      countryCode,
      setAttemptedDispensaryName,
      setDispensaryLoadLock,
    ],
  );

  const handleCardClick = (
    dispensary: DispensaryV3,
    index: number,
    ev: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    ev.preventDefault();

    publishEvent({
      action: "click",
      category: "Delivery Gate Flow",
      dispensaryId: dispensary.id,
      label: "dispensary card",
      merchandisingCampaignId: getMerchandisingCampaignId(
        dispensary.merchandisedCampaigns,
      ),
      trayNumber: index + 1,
    });

    setDispensaryLoadLock(true);
    setSelectedDispensary(dispensary);

    setModalVisible(true);
  };

  // * Primary effect for triggering delivery attempt
  // * from a desired dispensary after selecting
  // * on the pre-address page.
  useEffect(() => {
    if (isValidAddress && !!selectedDispensary) {
      tryDeliveryFromDispensary(selectedDispensary);
    }
  }, [isValidAddress, selectedDispensary, tryDeliveryFromDispensary]);

  const locationHeading =
    geoCityResult?.city && geoCityResult?.stateLong
      ? `${geoCityResult?.city}, ${geoCityResult?.stateLong}`
      : geoCityResult?.stateLong;

  let heading: string;
  if (headingOverride) {
    heading = headingOverride;
  } else if (locationHeading) {
    heading = `Order weed delivery in ${locationHeading}`;
  } else {
    heading = "Order weed delivery near you";
  }

  const getMerchandisingCampaignId = (
    campaigns: DispensaryV3MerchandisedCampaign[],
  ) => {
    const dpp = campaigns?.filter((campaign) => {
      return campaign.unitName === "delivery-platinum-placement";
    });

    return dpp?.[0]?.id;
  };

  return (
    <section
      className={classNames("container", className)}
      ref={modalReturnRef}
    >
      <AddressCaptureModal
        isVisible={modalVisible}
        onDismissed={() => setModalVisible(false)}
        returnFocusRef={modalReturnRef}
      />
      <h2 className="my-lg text-md font-extrabold">{heading}</h2>
      <div className="row">
        {dispensaries.map((dispensary, index) => {
          const {
            coverPhotoUrl,
            deliveryServiceAreaRanges,
            flags,
            id,
            logoUrl,
            merchandisedCampaigns,
            name,
            reviewCount,
            premiumRank,
            slug,
            starRating,
            timeZone,
          } = dispensary;

          return (
            <div className="col md:col-1/3 mb-md" key={slug}>
              <TrackImpression
                className="h-full"
                trackingFunction={() =>
                  publishEvent({
                    action: "impression",
                    category: "Delivery Gate Flow",
                    dispensaryId: id,
                    label: "dispensary card",
                    merchandisingCampaignId: getMerchandisingCampaignId(
                      merchandisedCampaigns,
                    ),
                    trayNumber: index + 1,
                  })
                }
              >
                <DispensaryCard
                  ctaOnClick={(ev) => handleCardClick(dispensary, index, ev)}
                  className="h-full"
                  currentStatus={dispensary.currentStatuses?.delivery}
                  deliveryEnabled={flags.includes("delivery")}
                  deliveryServiceAreaRanges={
                    deliveryServiceAreaRanges || undefined
                  }
                  dispensaryId={dispensary.id}
                  hideDeliveryFee={true}
                  isAd={isPlatinum(premiumRank)}
                  inStoreCartEnabled={dispensary.inStoreCartEnabled}
                  isNewDispensary={flags.includes("newDispensary")}
                  logoUrl={logoUrl || undefined}
                  name={name}
                  onClick={(ev) => handleCardClick(dispensary, index, ev)}
                  photoUrl={
                    isPlatinum(premiumRank)
                      ? coverPhotoUrl || undefined
                      : undefined
                  }
                  preorderConfigured={flags.includes("preorderConfigured")}
                  rating={starRating}
                  reviewCount={reviewCount}
                  scheduleType="delivery"
                  slug={slug}
                  timeZone={timeZone}
                  variant="platinum"
                  useLegacyFulfillmentText
                  flags={flags}
                />
              </TrackImpression>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PreaddressDispensaryCards;
